<template>
  <div>
    <DownloadModal :selected="selected" @download="onDownload" ref="downloadModal"/>
    <TagSelectorModal @add="addTags" ref="tagSelectorModal"/>
    <SetGalleryModal @set="setGallery" ref="setGalleryModal"/>
    <UploaderModal ref="uploaderModal" v-if="permissions.canUpload"/>
    <PublishEventModal ref="publishEventModal" v-if="hasPerm('event_wizard.publish')"/>
    <PContextMenu
        ref="contextMenu"
        :menu-items="contextItems"
        @item-selected="onContextMenuSelected"
    />

    <CRow v-if="!loaded">
      <CCol col="12" class="text-center my-4">
        <CSpinner color="primary"/>
      </CCol>
    </CRow>
    <template v-else>
      <CRow v-if="isDraft">
        <CCol>
          <PAlert color="warning"
                  extra-class="p-3"
                  :title="$pgettext('browse_photo', 'Activate Event')"
                  :text="$pgettext('browse_photo', 'To be able to upload photos you must first activate the event')"
                  icon="cipAlertTriangle">
            <template #cta>
              <CLink
                  @click="$refs.publishEventModal.open()"
                  v-if="hasPerm('event_wizard.publish')">
                <translate translate-context="browse_photo">Activate</translate>
                <CIcon name="cipArrowNarrowUpRight" class="ml-1" size="custom" width="20"/>
              </CLink>
            </template>
          </PAlert>
        </CCol>
      </CRow>
      <template v-else>
        <CRow v-if="uploadHelpText">
          <CCol col="12">
            <PAlert color="info"
                    class="mb-2"
                    dismissible
                    :text="uploadHelpText"
                    icon="cipInfo">
            </PAlert>
          </CCol>
        </CRow>
        <div
            @drop.prevent="drop" @dragover.prevent="dragover" @dragleave="dragleave"
            ref="preUploader"
            :class="{'is-dragging': isDragging}">
          <CRow v-if="hasUploadedMedia">
            <CCol col="12">
              <div class="d-flex justify-content-between mb-2">
                <div class="d-flex align-items-center">
                  <h1 v-translate translate-context="browse_photo" class="h5" v-if="in_tab">Medias</h1>
                  <h1 v-translate translate-context="browse_photo" v-else>Browse photos</h1>
                  <div class="ml-3" v-if="loaded">
                    <CButton @click="setScale('minus')" :disabled="scale <= 1" color="dark"
                             class="btn-circle btn-circle--sm mr-2" variant="outline" size="sm">
                      <CIcon name="cipMagnifierMinus"/>
                    </CButton>
                    <CButton @click="setScale('plus')" :disabled="scale >= 3" color="dark"
                             class="btn-circle btn-circle--sm" variant="outline" size="sm">
                      <CIcon name="cipMagnifierPlus"/>
                    </CButton>
                  </div>
                </div>
                <div v-if="loaded">
                  <FiltersBar :canUpload="permissions.canUpload" @filter="onFilter" @upload="openUploaderModal"/>
                </div>
              </div>
            </CCol>

            <CCol col="12" class="photo-browser" v-if="loaded">
              <CRow v-if="hasTimeline && !hideTimeline">
                <CCol col="12">
                  <TimeLine :activeKey="activeKey" @update:activeKey="onClickTimeLine"/>
                </CCol>
              </CRow>
              <CRow>
                <CCol :class="{ 'no-select': shiftPressed }">
                  <div class="photo-browser__medias" @dragover.prevent="onExternalDragOver">
                    <InfiniteScroll
                        @begin="fetchBefore"
                        @end="fetchAfter"
                        @update:visible="onChangeFirstVisibleMedia"
                        @selected="onSelect"
                        @unselected="onUnselect"
                        @hover="onHover"
                        @dragging="onDragging"
                        @dragOverMedia="onDragOverMedia"
                        @contextmenu="openContextMenu"
                        @download="onDownload"
                        :selected="selected"
                        :hovering="hovering"
                        :sortingPosition="sortingPosition"
                        :sortingDirection="dragDirection"
                        :timeZone="timeZone"
                        :has-before="hasBefore"
                        :has-after="hasAfter"
                        :scale="scale"
                        :in_tab="in_tab"
                        :has_timeline="hasTimeline && !hideTimeline"
                        :is_sorting="isSorting"
                        ref="infiniteScroll"
                    />
                  </div>
                </CCol>
              </CRow>
              <BottomBar
                  :selected="selected"
                  :actions="menuItems"
                  @action="onContextMenuSelected"
                  @resetSelected="onResetSelected"
                  @filter="onFilter"
                  @download="openDownloadModal"
              />
            </CCol>
          </CRow>
          <CRow :gutters="false" v-else>
            <CCol class="text-center pre-uploader d-flex justify-content-center align-items-center">
              <div>
                <CIcon class="text-primary d-block mx-auto mb-2" size="custom-size" :height="45" name="cipUploadCloud"/>
                <div>
                  <div class="font-weight-medium mb-1" v-translate>Drag the file here to upload</div>
                  <div class="text-help mb-4" style="font-size: 12px">(<span v-translate>Allowed formats:</span> .jpg
                    .png
                    .mov .mp4)
                  </div>
                </div>
                <CButton
                    @click="openUploaderModal"
                    size="sm" color="primary"
                    v-if="permissions.canUpload">
                  <CIcon name="cipUpload2" style="vertical-align: text-top"/>
                  <translate>Upload</translate>
                </CButton>
              </div>
            </CCol>
          </CRow>
        </div>
      </template>
    </template>


  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import TimeLine from "@/domain/photoSearch/components/TimeLine.vue";
import InfiniteScroll from "@/domain/photoSearch/components/InfiniteScroll.vue";
import BottomBar from "@/domain/photoSearch/components/BottomBar.vue";
import FiltersBar from "@/domain/photoSearch/components/FiltersBar.vue";
import DownloadModal from "@/domain/photoSearch/components/DownloadModal.vue";
import TagSelectorModal from "@/domain/photoSearch/components/TagSelectorModal.vue";
import SetGalleryModal from "@/domain/photoSearch/components/SetGalleryModal.vue";
import UploaderModal from "@/domain/photoSearch/components/UploaderModal.vue";
import PublishEventModal from "@/domain/events/components/PublishEventModal.vue";
import fileUtils from "@/domain/core/utils/fileUtils";
import PAlert from "@/components/PAlert.vue";
import PContextMenu from "@/domain/core/components/PContextMenu.vue";
import GallerySorting from "@/domain/core/constant/gallerySorting";
import {ValidationError} from "@/domain/core/exception/exceptions";
import MessageManager from "@/domain/core/utils/messageManager";

export default {
  name: 'PhotoSearch',
  components: {
    PContextMenu,
    PAlert,
    PublishEventModal,
    UploaderModal,
    SetGalleryModal,
    TagSelectorModal,
    DownloadModal,
    FiltersBar,
    BottomBar,
    InfiniteScroll,
    TimeLine
  },
  data() {
    return {
      scale: 1,
      selected: {},
      hovering: {},
      shiftPressed: false,
      activeKey: null,
      lastSelected: null,
      lastHovered: null,
      loaded: false,
      isDragging: false,
      isSorting: false,
      dragPosition: null,
      contextMedia: null,
    }
  },
  props: {
    gallery: {
      type: Object,
      required: false,
      default: null
    },
  },
  async mounted() {
    this.loaded = false
    this.setPicaServiceId({
      picaServiceId: this.$route.params.picaServiceId,
      galleryId: this.gallery?.id || null
    })
    if (this.gallery) await this.setForceToPublic(this.gallery.is_public)
    else await this.setForceToPublic(false)

    this.setTimezone(this.timeZone)
    await this.fetchConfig()
    if (this.hasTimeline) {
      await this.fetchTimeline()
      await this.$nextTick()
    }

    let promises = []
    promises.push(this.fetchMedia())
    promises.push(this.fetchGalleries())
    if (this.permissions.canUpdateTags) promises.push(this.fetchTags())
    await Promise.all(promises)

    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    MessageManager.onProcessed('photoSearch', this.newMediaProcessed)
    // MessageManager.onUploaded('photoSearch', this.newMediaUploaded)
    this.loaded = true
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    MessageManager.removeCallbacks('photoSearch')
  },
  computed: {
    ...mapGetters('photoSearch', [
      'hasTimeline',
      'hideTimeline',
      'timeline',
      'hasBefore',
      'hasAfter',
      'permissions',
      'media',
      'searchResult',
      'hasUploadedMedia',
      'sorting',
      'galleries',
      'availableTags',
      'activeFilters'
    ]),
    ...mapGetters('event', [
      'timeZone',
      'isDraft'
    ]),
    in_tab() {
      return this.galleryId !== null
    },
    uploadHelpText() {
      if (!this.permissions.canUpload) return null
      if (!this.gallery) return null
      if (this.gallery.is_public)
        return this.$pgettext('event_wizard.tutorial', 'Upload up to 20 items to this gallery to make them visible in the albums of all the participants.<br>Check the preview on the side to see how they will be displayed to participants.')
      return this.$pgettext('event_wizard.tutorial', 'Upload all event photos to be delivered to participants to this gallery. Each person will only receive and view the photos they appear in.')
    },
    uploadDisabled() {
      return !this.permissions.canUpload || this.isDraft
    }, sortingPosition() {
      return this.isSorting ? this.dragPosition?.order : null
    }, selectedMinOrder() {
      return Math.min(...Object.values(this.selected).map(photo => photo.order))
    }, dragDirection() {
      if (this.dragPosition?.order === this.selectedMinOrder) return 0
      return this.dragPosition?.order > this.selectedMinOrder ? 1 : -1
    }, canSort() {
      return this.sorting === GallerySorting.custom && this.permissions.canSortMedia
    }, canSetGallery() {
      if (!this.galleries) return false
      return this.galleries.filter(gallery => gallery.id !== this.gallery?.id).length > 0
    }, selectedMediaHasTags() {
      return Object.values(this.selected).some(photo => {
        return photo.tags.filter(tag => !tag.includes('g:')).length > 0
      })
    }, hasTagsToAdd() {
      if (this.validTagList.length === 0) return false
      return Object.values(this.selected).some(photo => {
        return this.validTagList.some(tag => !photo.tags.includes(tag))
      })
    }, validTagList() {
      if (!this.availableTags) return []
      return Object.keys(this.availableTags).reduce((acc, cat) => {
        Object.values(this.availableTags[cat]).forEach(tag => {
          acc.push(cat + ':' + tag)
        })
        return acc
      }, [])
    },
    menuItems() {
      let options = []
      if (this.canSort) {
        if (options.length)
          options.push({divider: true})
        options.push({value: 'move_top', label: this.$pgettext('photo_search.action', 'Move to Top')})
        options.push({value: 'move_bottom', label: this.$pgettext('photo_search.action', 'Move to End')})
      }
      if (this.canSetGallery) {
        if (options.length)
          options.push({divider: true})
        options.push({
          value: 'set_gallery',
          label: this.$pgettext('photo_search.action', 'Set Gallery'),
          icon: 'cipPencilLine'
        })
      }
      if (this.permissions.canUpdateTags) {
        if (options.length && (this.hasTagsToAdd || this.selectedMediaHasTags))
          options.push({divider: true})
        if (this.hasTagsToAdd)
          options.push({
            value: 'add_tags',
            label: this.$pgettext('photo_search.action', 'Add Tags'),
            icon: 'cipTagPlus'
          })
        if (this.selectedMediaHasTags)
          options.push({
            value: 'remove_tags',
            label: this.$pgettext('photo_search.action', 'Remove Tags'),
            icon: 'cipTagMinus'
          })
      }

      if (this.permissions.canDelete) {
        if (options.length)
          options.push({divider: true})
        options.push({
          value: 'delete_media',
          label: this.$pgettext('photo_search.action', 'Delete'),
          icon: 'cipTrashFull'
        })
      }
      return options
    },
    contextItems() {
      let options = []
      if (this.contextMedia !== null) {
        if (this.canSort) {
          options.push({
            value: 'move_before',
            label: this.$pgettext('photo_search.action', 'Move Before'),
            icon: 'cipArrowNarrowLeft'
          })
          options.push({
            value: 'move_after',
            label: this.$pgettext('photo_search.action', 'Move After'),
            icon: 'cipArrowNarrowRight'
          })
        }
        return options
      }
      if (this.canSort) {
        options.push({
          value: 'move_top',
          label: this.$pgettext('photo_search.action', 'Move to Top'),
          icon: 'cipArrowNarrowUp'
        })
        options.push({
          value: 'move_bottom',
          label: this.$pgettext('photo_search.action', 'Move to End'),
          icon: 'cipArrowNarrowDown'
        })
      }

      if (this.canSetGallery) {
        if (options.length)
          options.push({divider: true})
        options.push({
          value: 'set_gallery',
          label: this.$pgettext('photo_search.action', 'Set Gallery'),
          icon: 'cipPencilLine'
        })
      }

      if (this.permissions.canUpdateTags) {
        if (options.length && (this.hasTagsToAdd || this.selectedMediaHasTags))
          options.push({divider: true})
        if (this.hasTagsToAdd)
          options.push({
            value: 'add_tags',
            label: this.$pgettext('photo_search.action', 'Add Tags'),
            icon: 'cipTagPlus'
          })
        if (this.selectedMediaHasTags)
          options.push({
            value: 'remove_tags',
            label: this.$pgettext('photo_search.action', 'Remove Tags'),
            icon: 'cipTagMinus'
          })
      }

      if (this.permissions.canDownload) {
        if (options.length)
          options.push({divider: true})
        options.push({
          value: 'download',
          label: this.$pgettext('photo_search.action', 'Download'),
          icon: 'cipDownload03'
        })
      }

      if (this.permissions.canDelete) {
        if (options.length)
          options.push({divider: true})
        options.push({
          value: 'delete_media',
          label: this.$pgettext('photo_search.action', 'Delete'),
          icon: 'cipTrashFull'
        })
      }
      return options
    },
  },
  watch: {
    gallery: {
      immediate: true,
      handler() {
        this.setPicaServiceId({
          picaServiceId: this.$route.params.picaServiceId,
          galleryId: this.gallery?.id
        })
        if (this.gallery) this.setForceToPublic(this.gallery.is_public)
      }
    },
    async searchResult() {
      if (!this.searchResult) return

      let found = false
      if (this.$refs.infiniteScroll)
        found = this.$refs.infiniteScroll.scrollToMedia(this.searchResult.id)
      if (!found) {
        await this.fetchFromId(this.searchResult.id)
        await this.$nextTick()
        for (let i = 0; i < 5; i++) {
          await this.delay(100)
          found = this.$refs.infiniteScroll.scrollToMedia(this.searchResult.id)
          if (found) return
        }
      }
    },
  },
  methods: {
    ...mapActions('photoSearch', [
      'setPicaServiceId',
      'setTimezone',
      'downloadPhotos',
      'fetchConfig',
      'fetchTimeline',
      'fetchGalleries',
      'fetchBefore',
      'fetchAfter',
      'fetchFromId',
      'fetchForKey',
      'fetchFirstMedia',
      'setFilters',
      'setSearchQuery',
      'addTags',
      'removeTags',
      'setGallery',
      'newMediaUploaded',
      'newMediaUploaded',
      'reorder',
      'setPublicFlag',
      'setFreeFlag',
      'moveTop',
      'moveBottom',
      'deleteMedia',
      'fetchTags'
    ]),
    ...mapActions('uploader', [
      'setForceToPublic'
    ]),
    onSelect(photos) {
      if (this.shiftPressed) {
        let hovering = Object.keys(this.hovering)
        for (let i = 0; i < hovering.length; i++) {
          if (!this.selected[hovering[i]])
            this.$set(this.selected, hovering[i], hovering[i])
        }
        this.lastSelected = null
        this.lastHovered = null
      } else {
        for (let i = 0; i < photos.length; i++)
          if (!this.selected[photos[i].id])
            this.$set(this.selected, photos[i].id, photos[i])
        this.lastSelected = photos[photos.length - 1]
      }
    },
    onUnselect(photos) {
      if (this.shiftPressed) return

      for (let i = 0; i < photos.length; i++)
        if (this.selected[photos[i].id])
          this.$delete(this.selected, photos[i].id)
      this.lastSelected = null
    },
    onResetSelected() {
      this.selected = {}
      this.lastSelected = null
    },
    onChangeFirstVisibleMedia(key) {
      this.activeKey = key
    },
    onClickTimeLine(key) {
      this.activeKey = key
      const found = this.$refs.infiniteScroll.scrollToKey(key)
      if (!found)
        this.fetchMedia()
    },
    async fetchMedia() {
      if (this.hasTimeline && this.timeline.length) {
        if (!this.activeKey) this.activeKey = this.timeline[0].key
        await this.fetchForKey(this.activeKey)
      } else {
        await this.fetchFirstMedia()
      }

      this.lastHovered = null
      this.dragPosition = null
      this.lastSelected = null
    },
    async onFilter(filters) {
      this.setFilters(filters)
      let promises = []
      promises.push(this.fetchMedia())
      if (this.hasTimeline)
        promises.push(this.fetchTimeline())
      await Promise.all(promises)
    },
    onKeyDown(event) {
      if (event.key !== 'Shift') return
      this.shiftPressed = true
      this.updateHovering()
    },
    onKeyUp(event) {
      if (event.key !== 'Shift') return
      this.shiftPressed = false
      this.updateHovering()
    },
    onHover(id) {
      this.lastHovered = id
      this.updateHovering()
    },
    updateHovering() {
      if (!this.shiftPressed || !this.lastSelected || !this.lastHovered) {
        this.hovering = {}
        return
      }

      let lastSelectedIndex = this.media.findIndex(photo => photo.id === this.lastSelected.id)
      let lastHoveredIndex = this.media.findIndex(photo => photo.id === this.lastHovered)
      let start = Math.min(lastSelectedIndex, lastHoveredIndex)
      let end = Math.max(lastSelectedIndex, lastHoveredIndex)

      this.hovering = {}
      for (let i = start; i <= end; i++)
        this.$set(this.hovering, this.media[i].id, this.media[i].id)
    },
    setScale(action) {
      if (action === 'plus' && this.scale < 3) {
        this.scale++
      } else if (action === 'minus' && this.scale > 1) {
        this.scale--
      }
    },
    openDownloadModal() {
      this.$refs.downloadModal.open()
    },
    openAddTagsModal(ids) {
      this.$refs.tagSelectorModal.open(ids)
    },
    openSetGalleryModal(ids) {
      this.$refs.setGalleryModal.open(ids)
    },
    openUploaderModal() {
      this.$refs.uploaderModal.open()
    },
    async drop(e) {
      this.isDragging = false
      this.isSorting = false

      if (this.isFileDropEvent(e)) {
        if (this.uploadDisabled) return
        const files = await fileUtils.getFilesFromDropEvent(e)
        await this.$refs.uploaderModal.newFileDropped(files)
      } else {
        let sel = Object.keys(this.selected)
        await this.reorder({
          ids: sel,
          after: this.dragDirection > 0 ? this.dragPosition.id : null,
          before: this.dragDirection < 0 ? this.dragPosition.id : null
        })
        this.dragPosition = null
        this.selected = {}
      }
    },
    isFileDropEvent(e) {
      return e.dataTransfer.items.length > 0 && e.dataTransfer.items[0].kind === 'file'
    },
    dragover() {
      if (this.isSorting) return
      this.isDragging = true
    },
    dragleave(e) {
      //Prevents flickering during drag: Check if the related target is still within the parent element
      const related = e.relatedTarget
      if (related && this.$refs.preUploader.contains(related)) return

      if (this.isSorting) {
        this.dragPosition = this.media[0]
        return
      }

      this.isDragging = false
    },
    onDragging({event, photo}) {
      if (!this.canSort) return

      if (!this.selected[photo.id]) {
        this.selected = {}
        this.lastSelected = null
        this.$set(this.selected, photo.id, photo)
      }
      if (event.target.tagName === 'IMG')
        event.dataTransfer.setDragImage(event.target, 0, 0)
      this.isSorting = true
    },
    onDragOverMedia(photo) {
      this.dragPosition = photo
    },
    onExternalDragOver(event) {
      const target = event.target
      if (target.tagName === 'IMG')
        return false
      const validClasses = ['photo-browser__medias', 'infinite-scroll', 'j-cluster']
      if (!validClasses.some(className => target.classList.contains(className)))
        return false
      this.dragPosition = this.media[this.media.length - 1]
    },
    async openContextMenu({event, photo}) {
      if (Object.keys(this.selected).length > 0 && !this.selected[photo.id])
        this.contextMedia = photo // right click with something already selected, this is the target of the action
      else {
        this.contextMedia = null // right click on already select item, no target
        this.$set(this.selected, photo.id, photo)
      }
      await this.$nextTick()
      if (this.contextItems.length > 0)
        this.$refs.contextMenu.open(event, photo)
      else
        this.$refs.contextMenu.close()
    },
    async onContextMenuSelected(action) {
      if (this.selected.length <= 0)
        return this.notifyError(this.$pgettext('photo_search', 'Please select at least one file'))
      const ids = Object.keys(this.selected)
      try {
        if (action === 'set_public')
          await this.setPublicFlag({ids: ids, value: true})
        else if (action === 'remove_public')
          await this.setPublicFlag({ids: ids, value: false})
        else if (action === 'set_free')
          await this.setFreeFlag({ids: ids, value: true})
        else if (action === 'remove_free')
          await this.setFreeFlag({ids: ids, value: false})
        else if (action === 'move_top')
          await this.moveTop({ids: ids})
        else if (action === 'move_bottom')
          await this.moveBottom({ids: ids})
        else if (action === 'move_before')
          await this.reorder({ids: ids, before: this.contextMedia.id})
        else if (action === 'move_after')
          await this.reorder({ids: ids, after: this.contextMedia.id})
        else if (action === 'set_gallery')
          this.openSetGalleryModal(ids)
        else if (action === 'add_tags')
          this.openAddTagsModal(ids)
        else if (action === 'delete_media') {
          await this.confirm({
            color: 'danger',
            title: this.$pgettext('photo_search', 'Delete files'),
            message: this.$pgettext('photo_search', 'The selected content will be permanently and irreversibly removed from users\' albums.'),
            cb_confirm: () => {
              this.deleteMedia({ids: ids})
            }
          })
        } else if (action === 'remove_tags')
          await this.removeTags({ids: ids})
        else if (action === 'download')
          await this.downloadPhotos({ids: ids})
        else
          console.log('unknown action ' + action)
      } catch (e) {
        if (e instanceof ValidationError) {
          switch (e.getCode()) {
            case 'non_deletable_media':
              return await this.notifyError(
                  this.$gettextInterpolate(
                      this.$pgettext('photo_search', 'Some files cannot be deleted: %{ids}'),
                      {ids: e.getData().join(', ')}
                  )
              )
          }
        }
        throw e
      }
    },
    onDownload({ids, email, applyLogo}) {
      this.downloadPhotos({ids: ids, email: email, applyLogo: applyLogo})
    },
  }
}
</script>
<style scoped>
.no-select {
  user-select: none;
}
</style>